import React, { useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT, DURATION_MEDIUM, DURATION_SLOW } from '@/lib/motion';
import type { RateMultiplier } from '@/types/RateMultiplier';

import { isStraightTimeMultiplier, decimalToHHMM, type MultiplierLiveValues } from './multiplierUtils';
import { MultiplierListItem } from './MultiplierListItem';


interface MultiplierListProps {
  multipliers: RateMultiplier[];
  onEdit: (index: number) => void;
  onAdd: () => void;
  /** Index (in original multipliers array) of the multiplier being edited inline, or -1 for add mode, or null if closed */
  expandedIndex?: number | null;
  /** Rendered inline below the expanded card on desktop */
  inlineForm?: React.ReactNode;
  /** Live threshold from the add form, shown as a label above the inline form */
  liveAddThreshold?: number | null;
  /** Committed threshold (updated on blur/toggle), used for sort position */
  committedAddThreshold?: number | null;
  /** Live form values from the edit form, passed to the expanded MultiplierListItem */
  liveEditValues?: MultiplierLiveValues | null;
}

function ThresholdLabel({ threshold, visible = true }: { threshold: number | null; visible?: boolean }) {
  // Track last valid threshold so the label doesn't disappear during typing
  const [lastValid, setLastValid] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (threshold != null) setLastValid(threshold);
  }, [threshold]);
  const display = threshold ?? lastValid;

  // Always render the container for layout stability; hide text when not visible
  const label = display != null ? `after ${decimalToHHMM(display)}h` : '\u00A0';

  return (
    <div className={cn('hidden sm:block py-1 pl-1', !visible && 'invisible')}>
      <span className="text-[10px] sm:text-[12px] text-primary font-mono">
        {label}
      </span>
    </div>
  );
}

export function MultiplierList({ multipliers, onEdit, onAdd, expandedIndex, inlineForm, liveAddThreshold, committedAddThreshold, liveEditValues }: MultiplierListProps) {
  const addFormRef = useRef<HTMLDivElement>(null);

  // Sort: Straight Time pinned first, then by per-shift threshold ascending (null last),
  // then by weekly threshold ascending (null last), then by rate ascending.
  // Premium multipliers (non-STRAIGHT_TIME, no thresholds) are excluded — they are
  // managed via extended overtime tiers, not the overtime ladder.
  const sorted = useMemo(() => {
    const indexed = multipliers
      .map((m, i) => ({ m, i }))
      .filter(({ m }) =>
        isStraightTimeMultiplier(m.code) ||
        m.defaultPerShiftThresholdHours != null ||
        m.defaultPerWeekThresholdHours != null
      );
    indexed.sort((a, b) => {
      const aIsST = isStraightTimeMultiplier(a.m.code);
      const bIsST = isStraightTimeMultiplier(b.m.code);
      if (aIsST && !bIsST) return -1;
      if (!aIsST && bIsST) return 1;

      // Per-shift threshold ascending (null sorted last)
      const aShift = a.m.defaultPerShiftThresholdHours;
      const bShift = b.m.defaultPerShiftThresholdHours;
      if (aShift != null && bShift != null && aShift !== bShift) return aShift - bShift;
      if (aShift != null && bShift == null) return -1;
      if (aShift == null && bShift != null) return 1;

      // Weekly threshold ascending (null sorted last)
      const aWeek = a.m.defaultPerWeekThresholdHours;
      const bWeek = b.m.defaultPerWeekThresholdHours;
      if (aWeek != null && bWeek != null && aWeek !== bWeek) return aWeek - bWeek;
      if (aWeek != null && bWeek == null) return -1;
      if (aWeek == null && bWeek != null) return 1;

      // Fallback: rate ascending
      return (a.m.defaultMultiplier ?? 0) - (b.m.defaultMultiplier ?? 0);
    });
    return indexed;
  }, [multipliers]);

  const hasNonStraightTime = sorted.some(({ m }) => !isStraightTimeMultiplier(m.code));
  const isAddExpanded = expandedIndex === -1;

  // Compute insertion index for the add form based on committed threshold (blur/toggle)
  const addFormInsertIndex = useMemo(() => {
    if (!isAddExpanded || committedAddThreshold == null) return sorted.length;
    // Find the first non-straight-time multiplier with a higher per-shift threshold
    for (let i = 0; i < sorted.length; i++) {
      const m = sorted[i].m;
      if (isStraightTimeMultiplier(m.code)) continue;
      const t = m.defaultPerShiftThresholdHours;
      if (t != null && t > committedAddThreshold) return i;
      if (t == null) return i; // null thresholds sort last
    }
    return sorted.length;
  }, [isAddExpanded, committedAddThreshold, sorted]);

  const addFormElement = isAddExpanded && inlineForm ? (
    <motion.div
      key="add-form"
      ref={addFormRef}
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: DURATION_SLOW, ease: EASE_EXPO_OUT }}
      onAnimationComplete={(definition) => {
        if (definition === 'animate') {
          addFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }}
      className="overflow-hidden"
    >
      <ThresholdLabel threshold={liveAddThreshold ?? null} visible={committedAddThreshold != null} />
      <div className="border border-primary/30 border-l-2 border-l-primary rounded bg-surface-1 dark:bg-transparent">
        {inlineForm}
      </div>
    </motion.div>
  ) : null;

  // Build a flat list of render items so every element is a direct keyed child
  // of AnimatePresence — this lets layout animations handle reordering smoothly.
  const renderItems = useMemo(() => {
    const items: { key: string; type: 'card' | 'add-form'; sortedIdx: number; originalIndex: number; multiplier?: typeof sorted[0]['m'] }[] = [];
    sorted.forEach(({ m, i: originalIndex }, sortedIdx) => {
      if (sortedIdx === addFormInsertIndex && isAddExpanded) {
        items.push({ key: 'add-form', type: 'add-form', sortedIdx: -1, originalIndex: -1 });
      }
      items.push({ key: m.id ?? m.code, type: 'card', sortedIdx, originalIndex, multiplier: m });
    });
    if (addFormInsertIndex >= sorted.length && isAddExpanded) {
      items.push({ key: 'add-form', type: 'add-form', sortedIdx: -1, originalIndex: -1 });
    }
    return items;
  }, [sorted, addFormInsertIndex, isAddExpanded]);

  return (
    <div className="flex flex-col">
      {/* Cards + inline add form */}
      <div className="flex flex-col gap-1.5 sm:gap-0">
        <AnimatePresence>
          {renderItems.map((item) => {
            if (item.type === 'add-form') {
              return addFormElement;
            }

            const multiplier = item.multiplier!;
            const { sortedIdx, originalIndex } = item;
            const isStraightTime = isStraightTimeMultiplier(multiplier.code);
            const isExpanded = expandedIndex === originalIndex;
            // Use live edit values for threshold display when card is being edited
            const effectiveShiftThreshold = isExpanded && liveEditValues
              ? (liveEditValues.shiftEnabled ? liveEditValues.shiftThreshold : null)
              : (multiplier.defaultPerShiftThresholdHours ?? null);
            const hasThresholdSlot = sortedIdx > 0 && !isStraightTime;
            const hasThresholdValue = effectiveShiftThreshold != null;

            return (
              <motion.div key={item.key} layout transition={{ duration: DURATION_SLOW, ease: EASE_EXPO_OUT }}>
                {hasThresholdSlot && (
                  <ThresholdLabel threshold={effectiveShiftThreshold} visible={hasThresholdValue} />
                )}
                <div
                  className={cn(
                    'rounded',
                    isExpanded ? 'overflow-hidden border border-primary/60 border-l-2 border-l-primary bg-surface-1 dark:bg-transparent' : 'overflow-visible'
                  )}
                >
                  <MultiplierListItem
                    multiplier={multiplier}
                    isStraightTime={isStraightTime}
                    isExpanded={isExpanded}
                    onClick={isStraightTime ? undefined : () => onEdit(originalIndex)}
                    liveOverrides={isExpanded && liveEditValues ? liveEditValues : undefined}
                  />
                  <AnimatePresence>
                    {isExpanded && inlineForm && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: DURATION_SLOW, ease: EASE_EXPO_OUT }}
                        className="overflow-hidden"
                      >
                        {inlineForm}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {!isAddExpanded && (() => {
          const addBtn = (
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center gap-1 text-[10px] sm:text-[12px] font-mono text-primary/60 hover:text-primary transition-colors cursor-pointer border border-border/50 hover:border-primary/40 rounded px-3 py-1.5"
            >
              <Plus className="h-3 w-3" />
              Overtime Tier
            </button>
          );

          if (!hasNonStraightTime) {
            return (
              <motion.div
                key="add-btn"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
                className="flex flex-col items-center pt-2 gap-2 sm:pt-4 sm:gap-3 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={onAdd}
                  className="inline-flex items-center gap-1 text-xs font-mono text-primary hover:text-primary transition-colors cursor-pointer border border-primary/40 hover:border-primary/60 rounded px-4 py-2 animate-pulse-amber"
                >
                  <Plus className="h-3 w-3" />
                  Overtime Tier
                </button>
                <p className="text-xs font-mono text-muted-foreground italic">
                  <span className="text-primary/70">tip:</span> Day Rate crew typically bill 1.5x after their minimum billable hours.
                </p>
              </motion.div>
            );
          }

          return (
            <motion.div
              key="add-btn"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
              className="flex flex-col items-center pt-2 gap-2 sm:pt-4 sm:gap-3 overflow-hidden"
            >
              {addBtn}
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}
